import * as React from 'react';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { LinkRounded, RestartAlt } from '@mui/icons-material';
import { Box } from '@mui/material';
import {
  Bedtime,
  Block,
  Cancel,
  ContactMail,
  Delete,
  History,
  Payment,
  RestoreFromTrash,
  Undo,
} from '@mui/icons-material';
import { checkPermission } from '@/views/Facilities/CheckPermissions';
import { useMemo } from 'react';
import { REFUND_STATUSES } from '@/utility';
import { useTranslation } from 'react-i18next';
import { useConfirmation } from '@/context/ConfirmationContext';

const ITEM_HEIGHT = 48;

export default function LongMenu({
  handleCopyToClipboard,
  statusAction,
  handleRefundHistory,
  sendCheck,
  toggleDelete,
  refund,
  type = 'facility',
}) {
  const { t } = useTranslation();
  const { requestConfirmation } = useConfirmation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const enableCopyLink = useMemo(() => {
    return !refund.pay_immediately;
  }, [refund.pay_immediately]);

  const handleSetToDormant = () => {
    handleClose();
    requestConfirmation(
      'Are you sure you want to set this refund to dormant? This action will mark the refund as inactive.',
      (comment) => {
        statusAction({
          path: 'set-to-dormant/transactions',
          id: refund.id,
          comment,
        });
      },
      null,
      {
        showCommentField: true,
        commentLabel: 'Reason for setting to dormant',
        commentPlaceholder:
          'Please provide a reason for setting this refund to dormant...',
        commentRequired: true,
      },
    );
  };

  const handleStatusAction = (actionPath) => {
    handleClose();
    requestConfirmation('Are you sure you want to perform this action?', () => {
      statusAction({ path: actionPath, id: refund.id });
    });
  };

  return (
    <div>
      <IconButton
        aria-label='more'
        id='long-button'
        aria-controls={open ? 'long-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup='true'
        onClick={handleClick}
        size='large'>
        <MoreVertIcon />
      </IconButton>
      <Menu
        id='long-menu'
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            maxHeight: ITEM_HEIGHT * 10,
            minWidth: '250px',
          },
        }}>
        {enableCopyLink && (
          <MenuItem onClick={handleCopyToClipboard}>
            <Box display='flex' gap='10px'>
              <LinkRounded style={{ color: '#3AB795' }} />{' '}
              {t('payments.actions.copyLink')}
            </Box>
          </MenuItem>
        )}
        {checkPermission({
          parentKey: 'transaction_page',
          childKey: 'info_history',
          permission: 'read',
          facilityId: refund.facility_id,
        }) && (
          <MenuItem onClick={handleRefundHistory}>
            <Box display='flex' gap='10px'>
              <History style={{ color: '#3AB795' }} />{' '}
              {t('payments.actions.infoHistory')}
            </Box>
          </MenuItem>
        )}
        {checkPermission({
          parentKey: 'transaction_page',
          childKey: 'void_check',
          permission: 'read',
          facilityId: refund.facility_id,
        }) && (
          <MenuItem onClick={() => handleStatusAction('void-check')}>
            <Box display='flex' gap='10px'>
              <Block style={{ color: '#3AB795' }} />{' '}
              {t('payments.actions.voidCheck')}
            </Box>
          </MenuItem>
        )}
        {checkPermission({
          parentKey: 'transaction_page',
          childKey: 'cancel_refund',
          permission: 'read',
          facilityId: refund.facility_id,
        }) && (
          <MenuItem onClick={() => handleStatusAction('cancelRefund')}>
            <Box display='flex' gap='10px'>
              <Cancel style={{ color: '#3AB795' }} />{' '}
              {t('payments.actions.cancelRefund')}
            </Box>
          </MenuItem>
        )}
        {checkPermission({
          parentKey: 'transaction_page',
          childKey: 'cancel_payment_with_transfer',
          permission: 'write',
          facilityId: refund.facility_id,
        }) &&
          refund.is_facility_fbo && (
            <MenuItem onClick={() => handleStatusAction('cancelPayment')}>
              <Box display='flex' gap='10px'>
                <Undo style={{ color: '#3AB795' }} />{' '}
                {t('payments.actions.cancelPayment')}
              </Box>
            </MenuItem>
          )}
        {checkPermission({
          parentKey: 'transaction_page',
          childKey: 'move_to_ach_returned',
          permission: 'read',
          facilityId: refund.facility_id,
        }) && (
          <MenuItem onClick={() => handleStatusAction('move-to-ach-returned')}>
            <Box display='flex' gap='10px'>
              <RestartAlt style={{ color: '#3AB795' }} />
              {t('payments.actions.moveToAchReturned')}
            </Box>
          </MenuItem>
        )}
        {checkPermission({
          parentKey: 'transaction_page',
          childKey: 'move_to_unclaimed',
          permission: 'read',
          facilityId: refund.facility_id,
        }) && (
          <MenuItem onClick={() => handleStatusAction('move-to-unclaimed')}>
            <Box display='flex' gap='10px'>
              <RestartAlt style={{ color: '#3AB795' }} />
              {t('payments.actions.moveToUnclaimed')}
            </Box>
          </MenuItem>
        )}
        {checkPermission({
          parentKey: 'transaction_page',
          childKey: 'set_to_dormant',
          permission: 'read',
          facilityId: refund.facility_id,
        }) && (
          <MenuItem
            disabled={
              !(
                refund.refund_status === REFUND_STATUSES.UNCLAIMED ||
                refund.refund_status === REFUND_STATUSES.EXCEPTION
              )
            }
            onClick={handleSetToDormant}>
            <Box display='flex' gap='10px'>
              <Bedtime style={{ color: '#3AB795' }} />
              {t('payments.actions.setToDormant')}
            </Box>
          </MenuItem>
        )}
        {type === 'admin' && (
          <MenuItem onClick={toggleDelete}>
            <Box display='flex' gap='10px'>
              {refund.is_deleted ? (
                <RestoreFromTrash style={{ color: '#3AB795' }} />
              ) : (
                <Delete style={{ color: '#3AB795' }} />
              )}
              {refund.is_deleted ? 'Restore' : 'Delete'}
            </Box>
          </MenuItem>
        )}
        {checkPermission({
          parentKey: 'transaction_page',
          childKey: 'send_payment',
          permission: 'read',
          facilityId: refund.facility_id,
        }) && (
          <MenuItem onClick={sendCheck}>
            <Box display='flex' gap='10px'>
              <Payment style={{ color: '#3AB795' }} />
              {t('payments.actions.sendCheck')}
            </Box>
          </MenuItem>
        )}
        {type === 'admin' && (
          <MenuItem onClick={() => handleStatusAction('mark-check-completed')}>
            <Box display='flex' gap='10px'>
              <Payment style={{ color: '#3AB795' }} />
              {t('payments.actions.markCheckCompleted')}
            </Box>
          </MenuItem>
        )}
        {type === 'admin' && (
          <MenuItem
            onClick={() => handleStatusAction('retry-transaction')}
            disabled={!refund.transaction_is_error}>
            <Box display='flex' gap='10px'>
              <RestartAlt style={{ color: '#3AB795' }} />
              {t('payments.actions.retryTransaction')}
            </Box>
          </MenuItem>
        )}
        {type === 'admin' && (
          <MenuItem onClick={() => handleStatusAction('retry-communication')}>
            <Box display='flex' gap='10px'>
              <ContactMail style={{ color: '#3AB795' }} />
              {t('payments.actions.retryCommunication')}
            </Box>
          </MenuItem>
        )}
      </Menu>
    </div>
  );
}
